import { normalize } from 'normalizr';
import moment from 'moment-timezone';

import * as schema from './schema';
import * as types from './constants';
import { PUBNUB_CHANGED } from '../constants';
import Http from '../../http';
import {
  extractAppointments,
  formatAppointment,
} from '../../common/helpers/appointments';
import config from '../../../config';
import { Logger } from '../../../modules/infra/observability/logger';
import {
  RudderstackTracker,
  APPOINTMENT_MODIFIED,
  APPOINTMENT_BOOKED,
} from '../../../modules/infra/observability/rudderstack';
import { CustomerIoTracker, APPOINTMENT_CREATION_COMPLETED } from '../../../modules/infra/observability/customer-io';

const include = [
  'appointment_service',
  'customer',
  'customer.blockedBrands',
  'customer.blockedShops',
  'customer.blockedBarbers',
  'payment',
  'bookedBy',
  'discount',
  'promos.customer_promo.promo',
  'refunds',
  'costs',
  'shop',
].join(',');

const handleAppointmentData = (dispatch, type) => (response) => {
  if (response.error) {
    return response;
  }
  return Http.get(`/appointment/${response.id}`, { include }).then((appointment) => {
    dispatch({
      type,
      response: normalize(formatAppointment(appointment), schema.appointment),
    });
    return response;
  });
};

export function getAppointment(appointmentId, change) {
  return dispatch => Http.get(`/appointment/${appointmentId}`, { include }).then((appointment) => {
    if (!appointment) {
      Logger.log('Fetched empty appointment', {
        action: 'Fetched empty appointment',
        appointmentId,
        change,
      });
    }

    const formatted = formatAppointment(appointment);
    dispatch({
      type: types.GET_APPOINTMENT,
      response: normalize(formatted, schema.appointment),
      change,
    });
    dispatch({
      type: PUBNUB_CHANGED,
    });
    return formatted;
  });
}

export function addAppointment(shopId, data) {
  return dispatch => Http.post('/appointment', data)
    .then((response) => {
      RudderstackTracker.getInstance().track(APPOINTMENT_BOOKED);
      CustomerIoTracker.getInstance().track(APPOINTMENT_CREATION_COMPLETED);
      return handleAppointmentData(dispatch, types.ADD_APPOINTMENT)(response);
    });
}

export function updateAppointment(appointmentId, data) {
  return dispatch => Http.put(`/appointment/${appointmentId}?include=${include}`, data)
    .then((response) => {
      const appointment = formatAppointment(response);
      dispatch({
        type: types.UPDATE_APPOINTMENT,
        response: normalize(appointment, schema.appointment),
      });
      RudderstackTracker.getInstance().track(APPOINTMENT_MODIFIED);

      return appointment;
    });
}

export function cancelAppointment(appointmentId, cancelAll, sendNotifications) {
  const cancelAllBoolean = typeof cancelAll === 'boolean' && cancelAll;
  const data = { cancelAllRecurring: cancelAllBoolean, sendNotifications };

  return dispatch => Http.post(`/appointment/${appointmentId}/cancel`, data)
    .then(handleAppointmentData(dispatch, types.CANCEL_APPOINTMENT));
}

export function clearAppointments() {
  return dispatch => dispatch({ type: 'CLEAR_APPOINTMENTS' });
}

export function getAppointments(shopId, filtersFromParams = {}, page = 0) {
  const filters = Object.assign({}, filtersFromParams);

  filters.shopId = shopId;
  filters.include = include;
  filters.sortBy = 'createdAt:DESC,dateTime:ASC';

  return async (dispatch) => {
    dispatch({
      type: types.START_IMPORT_APPOINTMENTS,
    });
    filters.limit = types.LOAD_APPOINTMENTS_PER_PAGE;
    filters.skip = page * types.LOAD_APPOINTMENTS_PER_PAGE;
    let hasMore = true;
    let results = [];

    while (hasMore) {
      const appointments = await Http.get('/appointment', filters);
      results = [...results, ...appointments];
      hasMore = !(appointments.length < types.LOAD_APPOINTMENTS_PER_PAGE);
      filters.skip += types.LOAD_APPOINTMENTS_PER_PAGE;
    }

    return dispatch({
      type: types.IMPORT_APPOINTMENTS,
      response: normalize(extractAppointments(results), schema.arrayOfAppointments),
    });
  };
}

export function filterAppointments(filters) {
  return dispatch => dispatch({ type: 'FILTER_APPOINTMENTS', filters });
}

export const addBarberFilter = barber => ({
  type: types.ADD_BARBER_FILTER,
  barber: barber.id,
});

export const removeBarberFilter = barber => ({
  type: types.REMOVE_BARBER_FILTER,
  barber: barber.id,
});

export const showAllBarberFilters = barbers => ({
  type: types.SHOW_ALL_BARBER_FILTERS,
  barbers: barbers.map(barber => barber.id),
});

export const hideAllBarberFilters = () => ({
  type: types.HIDE_ALL_BARBER_FILTERS,
});

export const setDate = date => ({
  type: types.SET_DATE,
  date,
});

const DEFAULT_SCROLL_OFFSET_Y = 100;
const DEFAULT_HEADER_NODE_LEFT_PX = 90;
const TIMES_COLUMN_WIDTH_PX = 36;

export const scrollToAppointment = (dateTime, timezone, barberId) => (dispatch) => {
  const day = moment.tz(dateTime, timezone).format(config.dateFormatISO);
  const jumpToDate = () => dispatch(setDate(day));
  const jumpToTime = () => {
    const time = moment.tz(dateTime, timezone).format('HH:mm:ss');
    const timeAxisTimeNode = document.querySelector(`[data-time="${time}"]`);
    if (timeAxisTimeNode) {
      const timeSlotTop = timeAxisTimeNode.getBoundingClientRect().top + window.scrollY;

      const barberNamesHeaderNode = document.querySelector('.js-calendar-names');
      const tableTop = barberNamesHeaderNode
        ? barberNamesHeaderNode.getBoundingClientRect().bottom
        : DEFAULT_SCROLL_OFFSET_Y;

      window.scroll(0, timeSlotTop - tableTop);
    }
  };
  const jumpToBarber = () => {
    const barberNode = document.querySelector(`[data-barber="${barberId}"]`);
    if (barberNode) {
      const barberColumnLeft = barberNode.getBoundingClientRect().left + window.scrollX;

      const headerNode = document.querySelector('.js-calendar-names');
      const apptsTableLeft = headerNode
        ? headerNode.getBoundingClientRect().left + window.scrollX
        : DEFAULT_HEADER_NODE_LEFT_PX;

      const leftDistanceFromTimesEnd = barberColumnLeft - (apptsTableLeft + TIMES_COLUMN_WIDTH_PX);

      const sidebarNode = document.querySelector('[data-target-id="sidebar"]');
      const sidebarWidth = sidebarNode ? sidebarNode.getBoundingClientRect().right : 0;
      const sidebarToTableGap = apptsTableLeft - sidebarWidth;


      const scrollX = leftDistanceFromTimesEnd + sidebarToTableGap;
      window.scroll(scrollX, window.scrollY);
    }
  };

  jumpToDate();
  jumpToTime();
  jumpToBarber();
};

export const setStartOfWeek = date => ({
  type: types.SET_START_OF_WEEK,
  payload: date,
});

const loadCustomerNotesAction = notes => ({
  type: types.LOAD_CUSTOMER_NOTES,
  payload: notes,
});

// Ignore not found customer error because shop needs to be able to see
// appointment details for the customer they previously deleted
const ignoreIfNotFound = (err) => {
  if (err.status !== 404) {
    throw err;
  }
  return Promise.resolve({ notFound: true });
};

const loadCustomerNotes = url => dispatch =>
  Http.get(url, {}, false)
    .then((fetchedCustomer) => {
      dispatch(loadCustomerNotesAction(fetchedCustomer.notes));
      return fetchedCustomer;
    })
    .catch(ignoreIfNotFound);

export const loadOwnerCustomerNotes = (ownerId, ownerType, customerId) =>
  loadCustomerNotes(`/${ownerType}/${ownerId}/customer/${customerId}`);

export const setCustomerNotes = notes => ({
  type: types.SET_CUSTOMER_NOTES,
  payload: notes,
});

export const saveOwnerCustomer = (ownerId, ownerType, customerId, notes, leadId) => dispatch =>
  Http.put(`/${ownerType}/${ownerId}/customer/${customerId}`, { notes, leadId }, false)
    .catch(ignoreIfNotFound);

export const resetCustomerNotes = () => ({
  type: types.RESET_CUSTOMER_NOTES,
});

export const closeAppointmentModal = () => ({
  type: types.CLOSE_APPOINTMENT_MODAL,
});

export const setPromoCode = data => ({
  type: types.SET_PROMO_CODE,
  payload: data,
});

export const isRefundUnavailableError = (error) => {
  return error && error.message === 'Partial refunds are allowed only for captured appointments.';
};

export const refundAppointment = (appointmentId, refundOptions) => (dispatch) => {
  const shouldNotify = errInfo => !isRefundUnavailableError(errInfo);

  return Http.post(
    `/appointment/${appointmentId}/refund?include=${include}`,
    refundOptions,
    shouldNotify,
  ).then(response =>
    dispatch({
      type: types.UPDATE_APPOINTMENT,
      response: normalize(formatAppointment(response), schema.appointment),
    }),
  );
};

export const sendWaiver = appointmentId => dispatch => (
  Http.post(`/appointment/${appointmentId}/send-waiver`)
    .then((response) => {
      const appointment = formatAppointment(response);
      dispatch({
        type: types.UPDATE_APPOINTMENT,
        response: normalize(appointment, schema.appointment),
      });

      return appointment;
    })
);
